import 'swiper/css';
import 'swiper/css/pagination';

import Grid from '@mui/material/Grid';
import { useId } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ArticleCard from '@/components/BlogComponents/shared/ArticleCard/ArticleCard';
import ArticleHeroImage from '@/components/BlogComponents/shared/ArticleHeroImage/ArticleHeroImage';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { BlogPageType } from '@/contentful/api/api.types';
import useViewPortSize from '@/hooks/useViewportSize';
import { trimStringEndingPunctuation } from '@/utils/trimStringEndingPunctuation';

import useStyles from './BlogHighlight.style';

export interface BlogHighlightProps {
  primaryArticle?: BlogPageType;
  articlesCollection: BlogPageType[];
}

export default function BlogHighlight({
  primaryArticle,
  articlesCollection,
}: BlogHighlightProps): React.JSX.Element {
  const { classes, cx } = useStyles();

  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const paginationId = useId().replaceAll(':', '');
  const { isExtraSmall } = useViewPortSize();

  return (
    <SpacingContainer
      isFeatured
      className={classes.spacingContainer}
      data-testid='blogHighlight'
    >
      <GridContainer fixed>
        <div className={classes.container}>
          <Grid>
            {primaryArticle && <ArticleHeroImage article={primaryArticle} />}
          </Grid>
          {isExtraSmall ? (
            <>
              <Swiper
                navigation={{
                  prevEl: `#${prevElId}`,
                  nextEl: `#${nextElId}`,
                  disabledClass: classes.navigationButtonDisabled,
                }}
                className={classes.swiper}
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                pagination={{
                  el: `#${paginationId}`,
                  bulletActiveClass: 'bullet-active',
                  bulletClass: 'bullet',
                  horizontalClass: classes.horizontal,
                  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
                  renderBullet: (index: number, className: string) => {
                    return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="currentColor"/>
              </svg>`;
                  },
                }}
              >
                <div className={classes.cardContainer}>
                  {articlesCollection.map(
                    ({ blogTitle, abstract, path }, index) => (
                      <SwiperSlide
                        key={`${blogTitle}-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                          index
                        }`}
                      >
                        <Grid>
                          <ArticleCard
                            title={blogTitle}
                            body={`${trimStringEndingPunctuation(
                              abstract
                            )} ...`}
                            readMoreCta='Read More'
                            readMoreUrl={path || ''}
                          />
                        </Grid>
                      </SwiperSlide>
                    )
                  )}
                </div>
              </Swiper>
              <div
                className={cx(
                  classes.navigation,
                  articlesCollection.length < 2 && classes.hidden
                )}
              >
                <button
                  id={prevElId}
                  type='button'
                  aria-label='Previous article'
                  className={classes.navigationButton}
                >
                  <Icon name={IconName.ARROW_LEFT} width={24} />
                </button>
                <div id={paginationId} className={classes.pagination}></div>
                <button
                  id={nextElId}
                  type='button'
                  aria-label='Next article'
                  className={classes.navigationButton}
                >
                  <Icon name={IconName.ARROW_RIGHT} width={24} />
                </button>
              </div>
            </>
          ) : (
            <Grid>
              <div className={classes.cardContainer}>
                {articlesCollection.map(
                  ({ blogTitle, abstract, path }, index) => (
                    <ArticleCard
                      key={`${blogTitle}-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                        index
                      }`}
                      title={blogTitle}
                      body={`${trimStringEndingPunctuation(abstract)} ...`}
                      readMoreCta='Read More'
                      readMoreUrl={path || ''}
                    />
                  )
                )}
              </div>
            </Grid>
          )}
        </div>
      </GridContainer>
    </SpacingContainer>
  );
}
